// @flow

import React from 'react'
import styled from 'styled-components'
import { Visibility } from 'semantic-ui-react'
import Path from 'routes/path'
import Colors from 'components/styles/colors'
import { mediaMin } from 'frontend_core/dist/styles/media'
import { fontSize, spacing } from 'frontend_core/dist/styles/variables'
import { type FavoriteList } from 'types/favorite'
import { type PropTypes as FavoriteListItemPropTypes } from 'components/molecules/FavoriteListItem'
import Title from 'components/atoms/Title'
import MainContents from 'components/templates/MainContents'
import Loading from 'components/atoms/Loading'
import LoadingView from 'components/atoms/LoadingView'
import ModalForm from 'components/organisms/ModalForm'
import ModalSelect from 'components/organisms/ModalSelect'
import MenuLinkText from 'components/molecules/MenuLinkText'
import FavoriteSelectModal from 'components/organisms/FavoriteSelectModal'

const TitleWrapper = styled.div`
  margin-top: ${spacing.medium_2}px;
`

const SpaceNum = styled.p`
  margin-top: ${spacing.small_3}px;
  color: ${Colors.gray9};
  font-size: ${fontSize.small_3}px;
`

const FavoriteCardContent = styled.div`
  margin-top: ${spacing.medium_2}px;
  ${mediaMin.phone`
    margin-top: ${spacing.large}px;
  `};
`

const LoadingContainer = styled.div`
  padding: ${spacing.medium}px 0;
  text-align: center;
`

const FavoriteCardList = styled.ul`
  display: block;
  width: 100%;
  margin: 0;
`

export type PropTypes = {
  favoriteListDetail: FavoriteList,
  isLoading?: boolean,
  onReachedBottom: Function,
  isLoadingView?: boolean,
  children: any,
  errorMessage: string,
  loading: boolean,
  favoriteList: Array<FavoriteListItemPropTypes>,
  onClickBackLink: (e: Event) => void,
  onClickChangeListName: (e: Event) => void,
  negativeButtonOnClick: (e: Event) => void,
  onClickModalFormClose: (e: Event) => void,
  onChangeFavoriteListName: Function,
  onClickOpenInputFavorite: Function,
  onClickCreateFavoriteList: Function,
  onClickCloseInputFavorite: Function,
  closeDeleteFavoriteSelectModal: Function,
  openDeleteFavoriteSelectModal: Function,
  openChangeFavoriteListNameModal: Function,
  closeChangeFavoriteListNameModal: Function,
  onClickCloseSelectFavoriteListItemModal: Function,
  onClickCloseDeleteFavoriteListItemModal: Function,
  onClickDeleteFavoriteListItem: Function,
  onClickAddFavoriteList: Function,
  onClickRemoveFavoriteList: Function,
  registeredFavoriteLists: Function,
  onChangeChangeListName: Function,
  onClickDeleteFavoriteList: Function,
  changeFavoriteListName: string,
  isOpenInputFavorite: boolean,
  isOpenDeleteFavoriteSelectModal: boolean,
  isOpenChangeFavoriteListNameModal: boolean,
  errorMessageFavoriteSelectModal: string,
  selectFavoriteListItemModal: {
    isOpen: boolean,
    roomId: ?number,
  },
  deleteFavoriteListItemModal: {
    isOpen: boolean,
    roomId: ?number,
  },
}

export default class FavoriteListDetailPage extends React.Component<
  PropTypes,
  *,
> {
  props: PropTypes

  render() {
    const {
      isLoading,
      onReachedBottom,
      isLoadingView,
      children,
      errorMessage,
      onClickBackLink,
      onChangeFavoriteListName,
      onClickOpenInputFavorite,
      onClickCreateFavoriteList,
      onClickCloseInputFavorite,
      isOpenInputFavorite,
      favoriteList,
      loading,
      closeDeleteFavoriteSelectModal,
      openDeleteFavoriteSelectModal,
      isOpenDeleteFavoriteSelectModal,
      openChangeFavoriteListNameModal,
      closeChangeFavoriteListNameModal,
      isOpenChangeFavoriteListNameModal,
      deleteFavoriteListItemModal,
      onClickCloseSelectFavoriteListItemModal,
      selectFavoriteListItemModal,
      onClickCloseDeleteFavoriteListItemModal,
      errorMessageFavoriteSelectModal,
      onClickDeleteFavoriteListItem,
      onClickAddFavoriteList,
      onClickRemoveFavoriteList,
      registeredFavoriteLists,
      onChangeChangeListName,
      onClickChangeListName,
      onClickDeleteFavoriteList,
      changeFavoriteListName,
    } = this.props
    const {
      total_favorites: totalFavorites,
      favorites,
      name,
    } = this.props.favoriteListDetail

    return (
      <MainContents>
        <MenuLinkText
          onClickBackLink={onClickBackLink}
          onClickChangeName={openChangeFavoriteListNameModal}
          onClickDelete={openDeleteFavoriteSelectModal}
        />
        <TitleWrapper>
          <Title>{name}</Title>
        </TitleWrapper>
        <SpaceNum>
          {i18n.t('page.dashboard.favorite.NumberOfSpace', {
            spaceNum: typeof totalFavorites === 'number' ? totalFavorites : '-',
          })}
        </SpaceNum>
        {favorites && (
          <FavoriteCardContent>
            <FavoriteCardList>{children}</FavoriteCardList>
            <Visibility onBottomVisible={onReachedBottom} continuous />
          </FavoriteCardContent>
        )}
        {isLoading && (
          <LoadingContainer>
            <Loading />
          </LoadingContainer>
        )}
        {isLoadingView && <LoadingView />}
        <ModalForm
          isActive={isOpenChangeFavoriteListNameModal}
          title={i18n.t('page.dashboard.favorite.changeListName')}
          textList={[i18n.t('favorite.inputGuide')]}
          errorMessage={errorMessage}
          negativeButton={{
            text: i18n.t('page.shared.cancel'),
            onClick: closeChangeFavoriteListNameModal,
          }}
          positiveButton={{
            text: i18n.t('page.shared.change'),
            onClick: onClickChangeListName,
          }}
          placeholder={i18n.t('favorite.inputPlaceholder')}
          onClickClose={closeChangeFavoriteListNameModal}
          onChange={onChangeChangeListName}
          value={changeFavoriteListName}
        />
        <ModalSelect
          isActive={isOpenDeleteFavoriteSelectModal}
          title={i18n.t('page.dashboard.favorite.deleteList')}
          textList={[
            i18n.t('page.dashboard.favorite.deleteListDesc1'),
            i18n.t('page.dashboard.favorite.deleteListDesc2'),
          ]}
          negativeButton={{
            text: i18n.t('page.shared.cancel'),
            onClick: closeDeleteFavoriteSelectModal,
          }}
          positiveButton={{
            text: i18n.t('page.shared.delete'),
            onClick: onClickDeleteFavoriteList,
          }}
          onClickClose={closeDeleteFavoriteSelectModal}
        />
        <ModalSelect
          isActive={deleteFavoriteListItemModal.isOpen}
          title={i18n.t('page.dashboard.favorite.deleteFavorite')}
          textList={[
            i18n.t('page.dashboard.favorite.deleteFavoriteDesc1'),
            i18n.t('page.dashboard.favorite.deleteFavoriteDesc2'),
          ]}
          negativeButton={{
            text: i18n.t('page.shared.cancel'),
            onClick: onClickCloseDeleteFavoriteListItemModal,
          }}
          positiveButton={{
            text: i18n.t('page.shared.delete'),
            onClick: onClickDeleteFavoriteListItem,
          }}
          onClickClose={onClickCloseDeleteFavoriteListItemModal}
        />
        <FavoriteSelectModal
          show={selectFavoriteListItemModal.isOpen}
          loading={loading}
          favoriteList={favoriteList}
          registeredFavoriteLists={registeredFavoriteLists}
          onClickClose={onClickCloseSelectFavoriteListItemModal}
          openInputFavoriteName={isOpenInputFavorite}
          onChangeFavoriteListName={onChangeFavoriteListName}
          onClickOpenInputFavorite={onClickOpenInputFavorite}
          onClickCloseInputFavorite={onClickCloseInputFavorite}
          onClickCreateFavoriteList={onClickCreateFavoriteList}
          errorMessage={errorMessageFavoriteSelectModal}
          onClickAddFavoriteList={onClickAddFavoriteList}
          onClickRemoveFavoriteList={onClickRemoveFavoriteList}
        />
      </MainContents>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/pages/FavoriteListDetailPage/index.jsx